import Link from '@carvana/ds-link';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import { appAssetsCdnUrl } from '~/constants/environment';
import optimizeImage, { type Options } from '~/utilities/optimizeImage';
import { type NewCarVariant } from '~/utilities/srp/new-car/getNewCarVariant';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import { Wrapper } from '../PromoBanner.styles';

type OwnProps = {
  bannerName: string;
  href: string;
  make: string;
  model: string;
  variant: NewCarVariant;
};

const backgroundDesktopSrc = `${appAssetsCdnUrl}/images/new-car/hero-parent-model-background-large.svg`;
const backgroundMobileSrc = `${appAssetsCdnUrl}/images/helios/promoBanners/ncc/model/background-mobile.svg`;
const imageOptions: Options = { format: 'auto', quality: 90 };

export default function MakeModelBanner({
  bannerName,
  href,
  make,
  model,
  variant
}: OwnProps) {
  const shopAnalyticsAttributes = JSON.stringify({
    category: bannerName,
    label: 'Select Color, Options & More',
    variant
  });

  const vehicleBasePath = `${appAssetsCdnUrl}/images/new-car/${toLowerKebabCase(
    make
  )}/${toLowerKebabCase(model)}`;
  const vehicleDesktopSrc = optimizeImage(`${vehicleBasePath}-desktop.png`, {
    ...imageOptions,
    height: 200
  });
  const vehicleMobileSrc = optimizeImage(
    `${vehicleBasePath}-mobile.png`,
    imageOptions
  );

  return (
    <Wrapper className="bg-background-strong" data-testid="make-model-banner">
      <picture aria-hidden="true">
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={backgroundDesktopSrc}
        />
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          src={backgroundMobileSrc}
        />
      </picture>

      <div className="relative z-1 flex flex-1 flex-col justify-center gap-lg py-2xl pl-2xl sm:pr-0 lg:py-4xl lg:pl-4xl">
        <h4 className="t-heading-sm max-w-[185px] text-text-inverse sm:t-heading-xl sm:max-w-full md:max-w-[300px] lg:t-heading-2xl lg:max-w-full">
          Build your perfect new{' '}
          <span className="capitalize text-yellow-400">
            {make} {model}
          </span>
        </h4>
        <p className="hidden max-w-[348px] t-body-sm text-text-inverse sm:block sm:max-w-full">
          All brand new models — pickup or delivery as soon as today!
        </p>
        <div>
          <Link
            className="text-text-inverse"
            data-adobe-id="select-color-options-more"
            data-analytics-attributes={shopAnalyticsAttributes}
            data-analytics-id="Banner"
            data-testid="promo-banner-select-color-options-more"
            href={href}
            size="sm"
          >
            Select Color, Options & More
          </Link>
        </div>
      </div>

      <picture
        className="absolute bottom-0 right-0 z-10 w-[128px] sm:w-[250px] lg:w-[300px] lg:right-[60px]"
        data-testid="vehicle-photo"
      >
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={vehicleDesktopSrc}
        />
        <img
          alt={`${make} ${model}`}
          className="block w-full"
          src={vehicleMobileSrc}
        />
      </picture>
    </Wrapper>
  );
}
